import HLS from 'hls.js';
import { throttle } from 'lodash';

import loggerService from './logger.service';
import trackerService from './tracker.service';

import analytics from '@/analytics';
import store from '@/store';
import { Broadcast } from '@/store/modules/event/types';
import { handleOnDemandRunOfShow } from '@/utils/onDemandRunOfShow';
import { pushRoute } from '@/utils/routerAccess';

export let isOnDemandVideoPlaying = false;
export let videoSeekPosition = 0;
export let videoLength = 0;

let odLastSeekTime = 0; // timestamp of last onseeking — used for seek-induced buffer detection
let odPreviousHlsLevel = -1; // for ABR downgrade detection
let odPreviousPlaybackSpeed = 1; // last known user-selected speed — for ratechange filtering

const OD_USER_SPEEDS = new Set([0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]);

export const ON_DEMAND_VIDEO_CONTAINER_ID = 'onDemandVideoContainer';

export const PLAYBACK_TRACKING_THROTTLE_TIMEOUT = 10000;

function shouldHandleOnDemandRunOfShow(): boolean {
  return Boolean(store.state.event?.event?.on_demand_mode && store.state.event?.useOnDemandMQL);
}

export function initOnDemandHlsVideoElement(broadcast: Broadcast) {
  const hlsUrl =
    broadcast?.on_demand_asset?.client_hls_path ||
    broadcast?.on_demand_asset?.hls_path ||
    broadcast?.on_demand_asset?.fallback_hls_path ||
    broadcast?.on_demand_asset?.urls?.hls;

  if (!hlsUrl) {
    loggerService.error('On demand video asset not found!');
    return;
  }

  const videoElement = document.createElement('video');

  trackerService.addService('onDemandVideo');
  trackerService.track('onDemandVideo', 'shouldBeUsed', { isEnabled: true });
  trackerService.track('onDemandVideo', 'connectionInitialized', { timestamp: Date.now() });
  odLastSeekTime = 0;
  odPreviousHlsLevel = -1;
  odPreviousPlaybackSpeed = 1;

  videoElement.controls = true;
  videoElement.autoplay = true;

  // When the user scrubs the progress bar the browser emits: pause → seeking → seeked → play.
  // We suppress those seek-induced pause/play events so they aren't counted as user actions.
  let odJustSeeked = false;
  let odJustSeekedTimer: ReturnType<typeof setTimeout> | null = null;

  const handlePlaybackPlay = () => {
    handleOnDemandVideoPlaying();
    if (odJustSeeked) {
      odJustSeeked = false;
      if (odJustSeekedTimer) {
        clearTimeout(odJustSeekedTimer);
      }
      return;
    }
    analytics.track('od_user_action', {
      action: 'play',
      broadcast_id: broadcast.id,
      position_s: videoElement.currentTime
    });
  };
  videoElement.addEventListener('play', handlePlaybackPlay);
  const handlePlaybackPause = () => {
    // Ignore seek-induced pauses — browser emits pause → seeking → seeked → play on scrub
    if (videoElement.seeking) {
      return;
    }
    handleOnDemandVideoPaused();
    trackerService.track('onDemandVideo', 'pauseCount');
    analytics.track('od_user_action', {
      action: 'pause',
      broadcast_id: broadcast.id,
      position_s: videoElement.currentTime,
      completion_rate: videoLength > 0 ? videoElement.currentTime / videoLength : 0
    });
  };
  videoElement.addEventListener('pause', handlePlaybackPause);
  const handlePlaybackEnded = () => {
    handleOnDemandVideoEnded();
    analytics.track('od_user_action', {
      action: 'ended',
      broadcast_id: broadcast.id,
      position_s: videoElement.currentTime,
      completion_rate: videoLength > 0 ? videoElement.currentTime / videoLength : 0
    });
  };
  videoElement.addEventListener('ended', handlePlaybackEnded);
  const handleLoadedMetadata = () => {
    videoLength = videoElement.duration;
  };
  videoElement.onloadedmetadata = handleLoadedMetadata;

  // ── Playback speed change ────────────────────────────────────────────────────────
  const handleRateChange = () => {
    const newSpeed = videoElement.playbackRate;
    // Only count changes to standard user-selectable speeds — filters programmatic rate adjustments
    if (newSpeed !== odPreviousPlaybackSpeed && OD_USER_SPEEDS.has(newSpeed)) {
      trackerService.track('onDemandVideo', 'speedChangeCount');
      analytics.track('od_user_action', {
        action: 'speed_change',
        broadcast_id: broadcast.id,
        from_speed: odPreviousPlaybackSpeed,
        to_speed: newSpeed,
        position_s: videoElement.currentTime
      });
      odPreviousPlaybackSpeed = newSpeed;
    }
  };
  videoElement.addEventListener('ratechange', handleRateChange);

  // ── Seek tracking ────────────────────────────────────────────────────────
  let odLastPlaybackPosition = 0;

  const handleSeekPositionUpdate = () => {
    // to capture where the user was before they started seeking
    if (!videoElement.seeking) {
      odLastPlaybackPosition = videoElement.currentTime;
    }
  };
  videoElement.addEventListener('timeupdate', handleSeekPositionUpdate);

  const handleRunOfShowUpdate = throttle(() => {
    if (shouldHandleOnDemandRunOfShow()) {
      handleOnDemandRunOfShow(videoElement.currentTime);
    }
  }, 1000);
  videoElement.addEventListener('timeupdate', handleRunOfShowUpdate);

  const handleSeeking = () => {
    odLastSeekTime = Date.now();
  };
  videoElement.addEventListener('seeking', handleSeeking);

  const handleSeeked = () => {
    const fromPosition = odLastPlaybackPosition;
    const toPosition = videoElement.currentTime;

    // Suppress the auto-resume play event that always follows seeked
    odJustSeeked = true;
    if (odJustSeekedTimer) {
      clearTimeout(odJustSeekedTimer);
    }
    odJustSeekedTimer = setTimeout(() => {
      odJustSeeked = false;
    }, 300);

    // Filter HLS.js internal micro-seeks (gap fills / startup alignment < 1 s)
    if (Math.abs(toPosition - fromPosition) < 1) {
      return;
    }

    trackerService.track('onDemandVideo', 'seekCount');
    if (toPosition > fromPosition) {
      trackerService.track('onDemandVideo', 'forwardSkipCount');
    } else {
      trackerService.track('onDemandVideo', 'rewindCount');
    }
    analytics.track('od_seek', {
      broadcast_id: broadcast.id,
      from_position_s: fromPosition,
      to_position_s: toPosition,
      direction: toPosition > fromPosition ? 'forward' : 'rewind'
    });
  };
  videoElement.addEventListener('seeked', handleSeeked);

  // ── Buffer / stall tracking ──────────────────────────────────────────────
  const handleBufferStart = () => {
    const now = Date.now();
    const isSeekInduced = now - odLastSeekTime < 500;
    trackerService.track('onDemandVideo', 'bufferStart', { timestamp: now });
    analytics.track('od_buffer_start', {
      broadcast_id: broadcast.id,
      position_s: videoElement.currentTime,
      is_seek_induced: isSeekInduced,
      current_bandwidth_bps: (videoElement as any).__hlsInstance?.bandwidthEstimate ?? null
    });
  };
  videoElement.addEventListener('waiting', handleBufferStart);

  const handleCanPlay = () => {
    trackerService.track('onDemandVideo', 'bufferEnd', { timestamp: Date.now() });
  };
  videoElement.addEventListener('canplay', handleCanPlay);

  const handlePlaying = () => {
    trackerService.track('onDemandVideo', 'bufferEnd', { timestamp: Date.now() });
    trackerService.track('onDemandVideo', 'connectionEstablished', { timestamp: Date.now() });
  };
  videoElement.addEventListener('playing', handlePlaying);

  if (HLS.isSupported()) {
    const hls = new HLS({
      backBufferLength: 30,
      levelLoadingMaxRetry: 1000 // add this to arbitrary high number till VL is generating the correct captions submanifest
    });
    (videoElement as any).__hlsInstance = hls;
    hls.attachMedia(videoElement);
    hls.on(HLS.Events.MEDIA_ATTACHED, () => {
      hls.loadSource(hlsUrl);
      hls.on(HLS.Events.MANIFEST_PARSED, () => {
        videoElement.play();
      });
    });

    hls.on(HLS.Events.LEVEL_SWITCHED, (_event: any, data: any) => {
      const toLevel = data.level;
      const isDowngrade = odPreviousHlsLevel !== -1 && toLevel < odPreviousHlsLevel;
      trackerService.track('onDemandVideo', 'levelSwitchCount');
      if (isDowngrade) {
        trackerService.track('onDemandVideo', 'levelSwitchDownCount');
      }
      const bitrateBps = hls.levels?.[toLevel]?.bitrate ?? null;
      analytics.track('od_level_switched', {
        broadcast_id: broadcast.id,
        from_level: odPreviousHlsLevel,
        to_level: toLevel,
        bitrate_bps: bitrateBps,
        is_downgrade: isDowngrade
      });
      odPreviousHlsLevel = toLevel;
    });

    hls.on(HLS.Events.FRAG_LOADED, (_event: any, data: any) => {
      const loadTime = data?.stats?.loading?.end - data?.stats?.loading?.start;
      if (loadTime > 0) {
        trackerService.track('onDemandVideo', 'fragLoaded', { loadTime });
      }
    });

    hls.on(HLS.Events.FPS_DROP, (_event: any, data: any) => {
      trackerService.track('onDemandVideo', 'fpsDrop', { droppedFrames: data.droppedLevel ?? 0 });
      analytics.track('od_fps_drop', {
        broadcast_id: broadcast.id,
        dropped_frames: data.droppedLevel ?? 0,
        total_dropped_frames: data.totalDroppedFrames ?? 0
      });
    });

    hls.on(HLS.Events.ERROR, (event: any, data: any) => {
      analytics.track('od_error', {
        broadcast_id: broadcast.id,
        error_type: data.type,
        error_details: data.details,
        is_fatal: data.fatal
      });
      if (!data.fatal) {
        trackerService.track('onDemandVideo', 'nonFatalError');
      }
      if (videoElement.onerror) {
        videoElement.onerror(data);
      }
    });

    const handlePlaybackTimeUpdate = throttle((e: Event) => {
      videoSeekPosition = (e?.target as HTMLVideoElement)?.currentTime;
      analytics.track('on_demand_video_playing', {
        broadcastId: broadcast.id
      });
    }, PLAYBACK_TRACKING_THROTTLE_TIMEOUT);
    videoElement.addEventListener('timeupdate', handlePlaybackTimeUpdate);
  } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
    videoElement.src = hlsUrl;
    videoElement.setAttribute('playsinline', '');
  } else {
    loggerService.warn('HLS Playback is not supported on this device!');
  }
  return videoElement;
}

function handleOnDemandVideoPlaying() {
  isOnDemandVideoPlaying = true;
}

function handleOnDemandVideoPaused() {
  isOnDemandVideoPlaying = false;
}

function handleOnDemandVideoEnded() {
  isOnDemandVideoPlaying = false;

  if (store.getters['event/isWebinar']) {
    return;
  }

  const nextAgendaItemId = getNextOnDemandAgendaItem()?.id;

  if (nextAgendaItemId) {
    pushRoute({ params: { id: nextAgendaItemId } });
  } else {
    const params = store.state.route.params;
    pushRoute({ path: `/e/${params.eventID}/stage/agenda` });
  }
}

export function getNextOnDemandAgendaItem(): Broadcast | undefined {
  const broadcastsInAgenda = store.getters['event/userTrackAgenda'].filter((agendaItem: any) => {
    return !!agendaItem?.broadcast_type && agendaItem?.on_demand_asset;
  });

  const currentIndex = broadcastsInAgenda.findIndex(
    (agendaItem: Broadcast) => agendaItem.id === store.state.route.params.id
  );

  const nextAgendaItem = broadcastsInAgenda[currentIndex + 1];

  if (!nextAgendaItem) {
    return;
  }

  return nextAgendaItem;
}

export function cleanupOnDemandVideoElement(videoElement: HTMLVideoElement | null) {
  if (!videoElement) {
    return;
  }

  try {
    if (videoElement.pause) {
      videoElement.pause();
    }
    videoElement.src = '';
    videoElement.load();

    const hlsInstance = (videoElement as any).__hlsInstance;
    if (hlsInstance && typeof hlsInstance.destroy === 'function') {
      hlsInstance.destroy();
      (videoElement as any).__hlsInstance = null;
    }

    const container = document.getElementById(ON_DEMAND_VIDEO_CONTAINER_ID);
    if (container) {
      container.innerHTML = '';
    }

    if (videoElement.parentNode) {
      videoElement.parentNode.removeChild(videoElement);
    }
  } catch (error) {
    loggerService.error('Error cleaning up on-demand video element', error);
  }

  isOnDemandVideoPlaying = false;
}
